import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { eventDisplayName } from "../config/eventTypes";
import { formatDurationHuman } from "../utils/calendarUtils";
import {
	CARRIED_OVER_LABEL,
	CarryIcon,
	PULLED_AHEAD_LABEL,
} from "./CarryMarker";
import { type CalendarItem, itemColors } from "./EventBar";

// Same neutral surface as the Gantt and Saw tooltips; the status colour stays
// on the left accent and the status dot, like the bar and the Work Orders table
const CARD_BORDER = "#ddd";
const CARD_DIVIDER = "#e6e6e6";
const CARD_TEXT = "#333";
const CARD_LABEL = "#666";
const TAG_HEIGHT = 18;

export type SummaryField = { label: string; value: ReactNode };

const cardSx = (color: string, dropSlot = false) => ({
	boxSizing: "border-box",
	minWidth: 0,
	backgroundColor: "#fff",
	color: CARD_TEXT,
	border: `1px ${dropSlot ? "dashed" : "solid"} ${dropSlot ? color : CARD_BORDER}`,
	borderLeft: `4px solid ${color}`,
	borderRadius: "4px",
	...(dropSlot && { boxShadow: `0 0 0 3px ${alpha(color, 0.12)}` }),
});

// Label on the left, value on the right: one row per field
const FieldRows = ({
	fields,
	rowGap = "6px",
}: {
	fields: SummaryField[];
	rowGap?: string;
}) => (
	<Box
		sx={{
			display: "grid",
			gridTemplateColumns: "auto minmax(0, 1fr)",
			columnGap: 1.5,
			rowGap,
			alignItems: "baseline",
			minWidth: 0,
		}}
	>
		{fields.map((f) => (
			<Box key={f.label} sx={{ display: "contents" }}>
				<Typography
					noWrap
					sx={{ fontSize: "0.75rem", lineHeight: "20px", color: CARD_LABEL }}
				>
					{f.label}
				</Typography>
				<Typography
					noWrap
					component="div"
					title={typeof f.value === "string" ? f.value : undefined}
					sx={{
						fontSize: "0.875rem",
						fontWeight: 700,
						lineHeight: "20px",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{f.value}
				</Typography>
			</Box>
		))}
	</Box>
);

export const StatusValue = ({
	status,
	color,
}: {
	status: string;
	color: string;
}) => (
	<Box
		component="span"
		sx={{
			display: "inline-flex",
			alignItems: "center",
			color,
			fontWeight: 600,
		}}
	>
		<Box
			component="span"
			sx={{
				width: 8,
				height: 8,
				borderRadius: "50%",
				backgroundColor: color,
				mr: 0.75,
			}}
		/>
		{status}
	</Box>
);

/** The thing being changed, across the full width: field groups side by side,
 *  each one label-left / value-right */
export const WideSummaryCard = ({
	color,
	groups,
}: {
	color: string;
	groups: SummaryField[][];
}) => (
	<Box
		sx={{
			...cardSx(color),
			display: "grid",
			gridTemplateColumns: `minmax(0, 1.35fr) repeat(${Math.max(groups.length - 1, 0)}, minmax(0, 1fr))`,
			px: 2,
			py: 1.5,
		}}
	>
		{groups.map((fields, i) => (
			<Box
				key={fields.map((f) => f.label).join("|")}
				sx={{
					minWidth: 0,
					...(i > 0 && {
						pl: 2.5,
						ml: 2.5,
						borderLeft: `1px solid ${CARD_DIVIDER}`,
					}),
				}}
			>
				<FieldRows fields={fields} />
			</Box>
		))}
	</Box>
);

type SummaryCardProps = {
	title: ReactNode;
	/** accent: the work order status colour, or the event type colour */
	color: string;
	/** dot + text on the right of the header */
	status?: string | null;
	/** small neutral tag under the title. Passing it (even when it renders
	 *  nothing) reserves its row, so the cards of a sequence line up */
	tag?: ReactNode;
	fields?: SummaryField[];
	/** the slot the moved item will take: dashed outline with a soft halo */
	dropSlot?: boolean;
};

/** Compact card for a step of a sequence: title and status, then the fields */
export const SummaryCard = ({
	title,
	color,
	status,
	tag,
	fields = [],
	dropSlot = false,
}: SummaryCardProps) => (
	<Box
		sx={{
			...cardSx(color, dropSlot),
			// fills its slot, so the cards of a sequence share one height
			flex: 1,
			display: "flex",
			flexDirection: "column",
			gap: 1,
			px: 1.5,
			py: 1.25,
		}}
	>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
			<Typography
				noWrap
				title={typeof title === "string" ? title : undefined}
				sx={{ fontSize: "0.9375rem", fontWeight: 700, lineHeight: "22px" }}
			>
				{title}
			</Typography>
			{status && (
				<Box sx={{ ml: "auto", pl: 1, fontSize: "0.8125rem", flexShrink: 0 }}>
					<StatusValue status={status} color={color} />
				</Box>
			)}
		</Box>
		{tag !== undefined && (
			<Box sx={{ display: "flex", minHeight: TAG_HEIGHT }}>{tag}</Box>
		)}
		{fields.length > 0 && <FieldRows fields={fields} rowGap="4px" />}
	</Box>
);

type WorkOrderItem = Extract<CalendarItem, { kind: "wo" }>;

const CarryTag = ({ item }: { item: WorkOrderItem }) =>
	item.event.IsCarriedOver ? (
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				boxSizing: "border-box",
				height: TAG_HEIGHT,
				pl: 0.5,
				pr: 0.75,
				borderRadius: "3px",
				backgroundColor: "#f5f5f5",
				border: `1px solid ${CARD_BORDER}`,
				color: "#555",
				fontSize: "0.6875rem",
				fontWeight: 600,
			}}
		>
			<CarryIcon event={item.event} />
			{item.event.IsPulledAhead ? PULLED_AHEAD_LABEL : CARRIED_OVER_LABEL}
		</Box>
	) : null;

const partNumberFields = (item: WorkOrderItem): SummaryField[] => [
	{ label: "Part number", value: item.wo.PartNumber ?? "—" },
	...(item.wo.ExtendedPartNumber
		? [{ label: "Ext. part number", value: item.wo.ExtendedPartNumber }]
		: []),
];

/** The moved item across the modal: what it is, its order, and its state */
export const ItemMovingCard = ({
	item,
	duration,
}: {
	item: CalendarItem;
	duration: string;
}) => {
	const { color } = itemColors(item);
	if (item.kind === "wo")
		return (
			<WideSummaryCard
				color={color}
				groups={[
					partNumberFields(item),
					[
						{ label: "WO #", value: item.wo.WorkOrderNumber ?? "—" },
						{ label: "Quantity", value: item.wo.Quantity },
					],
					[
						{
							label: "Status",
							value: <StatusValue status={item.wo.Status} color={color} />,
						},
						{ label: "Duration", value: duration },
					],
				]}
			/>
		);
	return (
		<WideSummaryCard
			color={color}
			groups={[
				[
					{ label: "Event", value: eventDisplayName(item.event) },
					...(item.event.Comments
						? [{ label: "Comment", value: item.event.Comments }]
						: []),
				],
				[{ label: "Duration", value: duration }],
			]}
		/>
	);
};

/** A calendar item as a step of the landing sequence: work orders lead with
 *  the part number, the other events with their name. */
export const ItemSummaryCard = ({
	item,
	dropSlot,
}: {
	item: CalendarItem;
	dropSlot?: boolean;
}) => {
	const { color } = itemColors(item);
	if (item.kind === "wo")
		return (
			<SummaryCard
				title={item.wo.PartNumber ?? "—"}
				color={color}
				status={item.wo.Status}
				tag={<CarryTag item={item} />}
				fields={[
					{ label: "WO #", value: item.wo.WorkOrderNumber ?? "—" },
					{ label: "Quantity", value: item.wo.Quantity },
				]}
				dropSlot={dropSlot}
			/>
		);
	return (
		<SummaryCard
			title={eventDisplayName(item.event)}
			color={color}
			fields={[
				{ label: "Duration", value: formatDurationHuman(item.start, item.end) },
			]}
			dropSlot={dropSlot}
		/>
	);
};
